import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiGet, apiPatch } from '@/lib/api';

export type InterfaceMode = 'CLASSIC' | 'MODERN';
type Preference = { mode: InterfaceMode };
const InterfaceContext = createContext<{
  mode: InterfaceMode;
  pending: boolean;
  error: boolean;
  change: (mode: InterfaceMode) => void;
  retry: () => void;
}>({ mode: 'CLASSIC', pending: false, error: false, change: () => {}, retry: () => {} });

export function InterfaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const client = useQueryClient();
  const key = ['interface', user?.organizationId, user?.id];
  const preference = useQuery({
    queryKey: key,
    queryFn: () => apiGet<Preference>('/auth/interface'),
    enabled: !!user,
    staleTime: Infinity,
    retry: 1,
  });
  const mutation = useMutation({
    mutationFn: ({ mode }: { mode: InterfaceMode; key: typeof key }) =>
      apiPatch<Preference>('/auth/interface', { mode }),
    // Store against the identity captured by this mutation, never another account.
    onSuccess: (data, variables) => client.setQueryData(variables.key, data),
  });
  return (
    <InterfaceContext.Provider
      value={{
        mode: preference.data?.mode === 'MODERN' ? 'MODERN' : 'CLASSIC',
        pending: preference.isLoading || mutation.isPending,
        error: preference.isError || mutation.isError,
        change: (mode) => {
          if (!mutation.isPending && !preference.isLoading) mutation.mutate({ mode, key });
        },
        retry: () => {
          mutation.reset();
          void preference.refetch();
        },
      }}
    >
      {children}
    </InterfaceContext.Provider>
  );
}
export const useInterface = () => useContext(InterfaceContext);
